import { MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createRoot, Root } from "react-dom/client"
import { useSearchParams } from "react-router-dom"
import WebViewer from "@pdftron/webviewer"
import { useQueryClient } from "@tanstack/react-query"
import { useViewerStore } from "store/viewer.store"
import { useGetQuotation, useDuplicateFurniture } from "hooks/api/quotation/quotations.hook"
import { useMessage } from "hooks/message.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import {
	insertFurnitureIntoCache,
	reorderFurnitureInCache,
	spliceErefIntoCache,
} from "helpers/quotationCache.helper"
import { copyFurniture, readFurnitureClipboard, hasClipboardFor } from "helpers/furnitureClipboard.helper"
import { computeDuplicatePlacement } from "helpers/furniturePlacement.helper"
import { QuotationContextMenu } from "./QuotationContextMenu/QuotationContextMenu"
import type { TContextMenuItem } from "./QuotationContextMenu/QuotationContextMenu"
import { CustomWebViewerHeader } from "./CustomWebViewerHeader/CustomWebViewerHeader"
import { ElevationReferencesSidebar } from "./ElevationReferencesSidebar/ElevationReferencesSidebar"
import { ElevationReferencesToggle } from "./ElevationReferencesSidebar/ElevationReferencesToggle/ElevationReferencesToggle"
import { ReferenceSidebar } from "./ReferenceSidebar/ReferenceSidebar"
import { FurnitureSidebar } from "./FurnitureSidebar/FurnitureSidebar"
import { EcallSidebar } from "./EcallSidebar/EcallSidebar"
import { JumpingNavigationBar } from "./JumpingNavigationBar/JumpingNavigationBar"
import { PageNavigationBar } from "./PageNavigationBar/PageNavigationBar"
import leftSidebarStyles from "./ElevationReferencesSidebar/ElevationReferencesSidebar.module.scss?inline"
import toggleStyles from "./ElevationReferencesSidebar/ElevationReferencesToggle/ElevationReferencesToggle.module.scss?inline"
import rightSidebarStyles from "./ReferenceSidebar/ReferenceSidebar.module.scss?inline"
import numberAndPageStyles from "./ReferenceSidebar/layout/DetailsTab/layout/NumberAndPage/NumberAndPage.module.scss?inline"
import elevationNoteStyles from "./ReferenceSidebar/layout/DetailsTab/layout/ElevationNote/ElevationNote.module.scss?inline"
import objectsStyles from "./ReferenceSidebar/layout/DetailsTab/layout/Objects/Objects.module.scss?inline"
import elevationCalloutsStyles from "./ReferenceSidebar/layout/DetailsTab/layout/ElevationCallouts/ElevationCallouts.module.scss?inline"
import connectedCalloutsListStyles from "./shared/ConnectedCalloutsList/ConnectedCalloutsList.module.scss?inline"
import connectedCalloutsTabStyles from "./FurnitureSidebar/layout/ConnectedCalloutsTab/ConnectedCalloutsTab.module.scss?inline"
import jumpingNavigationBarStyles from "./JumpingNavigationBar/JumpingNavigationBar.module.scss?inline"
import pageNavigationBarStyles from "./PageNavigationBar/PageNavigationBar.module.scss?inline"
import ecallSidebarStyles from "./EcallSidebar/EcallSidebar.module.scss?inline"
import elevationReferenceRowStyles from "./EcallSidebar/ElevationReferenceRow/ElevationReferenceRow.module.scss?inline"
import FurnitureModelLabel from "./FurnitureModelLabel/FurnitureModelLabel.module.scss?inline"
import seriesAndDeliveryStyles from "./ReferenceSidebar/layout/ConfigurationTab/layout/SeriesAndDelivery/SeriesAndDelivery.module.scss?inline"
import slopeAndOptionsStyles from "./ReferenceSidebar/layout/ConfigurationTab/layout/SlopeAndOptions/SlopeAndOptions.module.scss?inline"
import countertopSeriesStyles from "./ReferenceSidebar/layout/ConfigurationTab/layout/CountertopSeries/CountertopSeries.module.scss?inline"
import footerBarStyles from "./FooterBar/FooterBar.module.scss?inline"
import useReviewableStyles from "./FooterBar/useReviewable.module.scss?inline"
import furnitureSidebarStyles from "./FurnitureSidebar/FurnitureSidebar.module.scss?inline"
import furnitureHeaderStyles from "./FurnitureSidebar/layout/FurnitureHeader/FurnitureHeader.module.scss?inline"
import furnitureImageStyles from "./FurnitureSidebar/layout/FurnitureImage/FurnitureImage.module.scss?inline"
import erefSelectStyles from "./FurnitureSidebar/layout/ElevationReferenceSelect/ElevationReferenceSelect.module.scss?inline"
import categoriesSectionStyles from "./FurnitureSidebar/layout/CategoriesSection/CategoriesSection.module.scss?inline"
import dimensionsSectionStyles from "./FurnitureSidebar/layout/DimensionsSection/DimensionsSection.module.scss?inline"
import modelNumberSectionStyles from "./FurnitureSidebar/layout/ModelNumberSection/ModelNumberSection.module.scss?inline"
import selectModelNumberModalStyles from "./FurnitureSidebar/layout/ModelNumberSection/SelectModelNumberModal/SelectModelNumberModal.module.scss?inline"
import modelNumberRowStyles from "./FurnitureSidebar/layout/ModelNumberSection/SelectModelNumberModal/ModelNumberRow.module.scss?inline"
import priceSectionStyles from "./FurnitureSidebar/layout/PriceSection/PriceSection.module.scss?inline"
import nameSectionStyles from "./FurnitureSidebar/layout/NameSection/NameSection.module.scss?inline"
import attachmentsSectionStyles from "./FurnitureSidebar/layout/AttachmentsSection/AttachmentsSection.module.scss?inline"
import numberInputRowStyles from "./FurnitureSidebar/layout/AttachmentsSection/NumberInputRow.module.scss?inline"
import segmentedCountRowStyles from "./FurnitureSidebar/layout/AttachmentsSection/SegmentedCountRow.module.scss?inline"
import notesSectionStyles from "./FurnitureSidebar/layout/NotesSection/NotesSection.module.scss?inline"
import furnitureFooterStyles from "./FurnitureSidebar/layout/FurnitureFooter/FurnitureFooter.module.scss?inline"
import backButtonStyles from "./shared/BackButton/BackButton.module.scss?inline"
import prevNextNavStyles from "./shared/PrevNextNav/PrevNextNav.module.scss?inline"
import buttonPrimaryStyles from "components/ui/buttons/ButtonPrimary/ButtonPrimary.module.scss?inline"
import buttonSecondaryStyles from "components/ui/buttons/ButtonSecondary/ButtonSecondary.module.scss?inline"
import buttonOutlineStyles from "components/ui/buttons/ButtonOutline/ButtonOutline.module.scss?inline"
import buttonDangerStyles from "components/ui/buttons/ButtonDanger/ButtonDanger.module.scss?inline"
import confirmModalStyles from "components/ui/modals/ConfirmModal/ConfirmModal.module.scss?inline"
import { useAnnotations } from "hooks/annotations.hook"
import AnnotationService from "services/annotations.service"
import { StyleProvider } from "@ant-design/cssinjs"
import { ShadowDomProviders } from "./ShadowDomProviders/ShadowDomProviders"
import contextMenuStyles from "./QuotationContextMenu/QuotationContextMenu.module.scss?inline"
import type { AxiosResponse } from "axios"
import { useEditorHotkeys } from "./hotkeys/useEditorHotkeys"
import hotkeyFocusStyles from "./hotkeys/hotkeyFocus.module.scss?inline"
import type { WebViewerInstance } from "@pdftron/webviewer"
import type {
	TEcall,
	TErefWithFurniture,
	TFurniture,
	TDuplicateFurnitureRequest,
	TDuplicateFurnitureResponse,
} from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TViewerMode } from "store/viewer.store"

const licenseKey = import.meta.env.VITE_WEBVIEWER_KEY

// Toolbar groups hidden in preview mode so the read-only preview matches the Documents-tab
// DocumentModal (no annotate/edit affordances).
const PREVIEW_DISABLED_ELEMENTS = [
	"toolbarGroup-Annotate",
	"toolbarGroup-Insert",
	"toolbarGroup-Edit",
	"toolbarGroup-FillAndSign",
	"toolbarGroup-Forms",
	"notesPanelToggle",
]

type Props = {
	fileLink: string | undefined
	previewRef: MutableRefObject<HTMLElement | null>
	isModalOpen: boolean
	mode: TViewerMode
	onClose?: () => void
}

const injectStyles = (shadowRoot: ShadowRoot, css: string) => {
	const styleEl = document.createElement("style")
	styleEl.textContent = css
	shadowRoot.appendChild(styleEl)
}

type RootEntry = { root: Root; container: HTMLElement }

// Overhang in px that extends the jumping nav bar past the document area on each
// side so it visually covers the panel-resizer gutters next to the sidebars.
const JUMPING_NAV_SIDE_OVERHANG = 13

const positionJumpingNavOverlay = (
	wrapper: HTMLElement | null | undefined,
	docEl: HTMLElement | null | undefined,
	targetEl: HTMLElement | null | undefined,
) => {
	if (!wrapper || !docEl || !targetEl) return
	const docRect = docEl.getBoundingClientRect()
	const targetRect = targetEl.getBoundingClientRect()
	wrapper.style.left = `${docRect.left - targetRect.left - JUMPING_NAV_SIDE_OVERHANG}px`
	wrapper.style.top = `${docRect.top - targetRect.top}px`
	wrapper.style.width = `${docRect.width + JUMPING_NAV_SIDE_OVERHANG * 2}px`
}

const PAGE_NAV_BOTTOM_OFFSET = 24
// Idle delay (ms) before the bar fades out — matches Apryse's default indicator UX.
const PAGE_NAV_HIDE_DELAY = 2000
// How long to honor the chevron / page-name target after a programmatic navigation,
// so Apryse's scroll-animation pageNumberUpdated bursts can't snap us back.
const PAGE_NAV_TARGET_LOCK_MS = 1000
// Delay (ms) after entering editor mode before re-positioning the floating overlays — long
// enough to outlast the editor modal's open animation, after which getBoundingClientRect
// returns untransformed rects. Ant Design's modal motion is ~300ms.
const MODAL_ANIM_SETTLE_MS = 350

const positionPageNavOverlay = (
	wrapper: HTMLElement | null | undefined,
	docEl: HTMLElement | null | undefined,
	targetEl: HTMLElement | null | undefined,
) => {
	if (!wrapper || !docEl || !targetEl) return
	const docRect = docEl.getBoundingClientRect()
	const targetRect = targetEl.getBoundingClientRect()
	const centerX = docRect.left + docRect.width / 2 - targetRect.left
	const bottomY = docRect.bottom - targetRect.top - PAGE_NAV_BOTTOM_OFFSET
	wrapper.style.left = `${centerX}px`
	wrapper.style.top = `${bottomY}px`
}

const getEcallsForErefId = (ecalls: TEcall[], erefId: number | null | undefined): TEcall[] => {
	if (erefId == null) return []
	return ecalls.filter((e) => e.elevation_numbers.some((en) => en.eref_id === erefId))
}

const safeRender = (entryRef: MutableRefObject<RootEntry | null>, element: ReactNode): boolean => {
	const entry = entryRef.current
	if (!entry || !entry.container.isConnected) return false
	try {
		entry.root.render(element)
		return true
	} catch (error) {
		console.warn("WebViewer shadow render skipped:", error)
		return false
	}
}

const disposeRoot = (entryRef: MutableRefObject<RootEntry | null>) => {
	const entry = entryRef.current
	entryRef.current = null
	if (!entry) return
	queueMicrotask(() => {
		try {
			entry.root.unmount()
		} catch {
			/* root already torn down */
		}
	})
}

const createPanelControls = (
	instanceRef: MutableRefObject<WebViewerInstance | null>,
	dataElement: string,
	setIsOpen: (open: boolean) => void,
) => ({
	open: () => {
		if (instanceRef.current) {
			instanceRef.current.UI.openElements([dataElement])
			setIsOpen(true)
		}
	},
	close: () => {
		if (instanceRef.current) {
			instanceRef.current.UI.closeElements([dataElement])
			setIsOpen(false)
		}
	},
})

export const useQuotationViewer = ({ fileLink, previewRef, isModalOpen, mode, onClose }: Props) => {
	const instanceRef = useRef<WebViewerInstance | null>(null)
	// Set synchronously the moment init starts. instanceRef.current is only assigned
	// after the async WebViewer(...) promise resolves, so this guards the window in
	// between against a re-run of the init effect mounting a second viewer.
	const isInitializingRef = useRef(false)
	const { registerQuotationInstance, openQuotationFeedback, openQuotationSettings, openFeedbackPanel, setQuotationViewerOpen } =
		useViewerStore()
	// Publish whether the fullscreen viewer is open so the hosting FileInfo drawer can stop closing
	// on Escape while it covers the drawer (Escape is owned by the viewer). Reset on unmount.
	useEffect(() => {
		setQuotationViewerOpen(isModalOpen)
		return () => setQuotationViewerOpen(false)
	}, [isModalOpen, setQuotationViewerOpen])
	// Latest mode, readable inside async init / event handlers without re-subscribing.
	const modeRef = useRef<TViewerMode>(mode)
	modeRef.current = mode
	// Guards the one-time "Share feedback" button injection into the default (preview) header.
	const previewFeedbackButtonAddedRef = useRef(false)
	// Guards the one-time registration of the editor's custom header. Both the default (preview)
	// and custom (editor) headers stay registered for the life of the single shared instance;
	// mode switching only toggles their visibility. See applyHeaderAndTools. #1119
	const customHeaderAddedRef = useRef(false)
	const loadingTaskRef = useRef<Promise<unknown> | null>(null)
	const [isInitialized, setIsInitialized] = useState(false)
	const headerEntryRef = useRef<RootEntry | null>(null)
	const sidebarLeftEntryRef = useRef<RootEntry | null>(null)
	const sidebarRightEntryRef = useRef<RootEntry | null>(null)
	const toggleEntryRef = useRef<RootEntry | null>(null)
	const jumpingNavEntryRef = useRef<RootEntry | null>(null)
	const jumpingNavResizeObserverRef = useRef<ResizeObserver | null>(null)
	const jumpingNavDocAreaRef = useRef<HTMLElement | null>(null)
	const jumpingNavTargetContainerRef = useRef<HTMLElement | null>(null)
	// Recomputes the floating-overlay (page-nav + jumping-nav) positions from the live
	// element rects. Stored in a ref so effects can trigger it after a mode switch — the
	// ResizeObserver alone misses it (a modal scale-animation changes getBoundingClientRect
	// but not layout size, so it never re-fires once the animation settles). #1119
	const repositionOverlaysRef = useRef<(() => void) | null>(null)
	const pageNavEntryRef = useRef<RootEntry | null>(null)
	const pageNavHideTimerRef = useRef<number | null>(null)
	const isPageNavFocusedRef = useRef(false)
	const isPageNavVisibleRef = useRef(false)
	const pageNavTargetRef = useRef<number | null>(null)
	const pageNavTargetClearTimerRef = useRef<number | null>(null)
	const pageNavScrollElRef = useRef<HTMLElement | null>(null)
	const pageNavScrollHandlerRef = useRef<(() => void) | null>(null)
	const [currentPage, setCurrentPage] = useState(1)
	const [totalPages, setTotalPages] = useState(0)
	const [isPageNavVisible, setIsPageNavVisible] = useState(false)
	const [isPanelOpen, setIsPanelOpen] = useState(false)
	const [isReferencePanelOpen, setIsReferencePanelOpen] = useState(false)
	// Latest open flag, readable inside memoized hotkey callbacks. rightPanelMode is not reset when
	// the sidebar is closed via its X, so navigation must also confirm the panel is actually open.
	const isReferencePanelOpenRef = useRef(isReferencePanelOpen)
	isReferencePanelOpenRef.current = isReferencePanelOpen
	const [isDocumentLoaded, setIsDocumentLoaded] = useState(false)
	const [selectedEref, setSelectedEref] = useState<TErefWithFurniture | undefined>(undefined)
	const selectedErefRef = useRef<TErefWithFurniture | undefined>(undefined)
	const [rightPanelMode, setRightPanelMode] = useState<"eref" | "furniture" | "ecall" | null>(null)
	const rightPanelModeRef = useRef<"eref" | "furniture" | "ecall" | null>(null)
	rightPanelModeRef.current = rightPanelMode
	const [selectedFurniture, setSelectedFurniture] = useState<TFurniture | undefined>(undefined)
	const selectedFurnitureRef = useRef<TFurniture | undefined>(undefined)
	const [selectedEcall, setSelectedEcall] = useState<TEcall | undefined>(undefined)
	const selectedEcallRef = useRef<TEcall | undefined>(undefined)

	type TJumpSource =
		| { kind: "furniture"; furniture: TFurniture }
		| { kind: "eref"; eref: TErefWithFurniture }
		| { kind: "ecall"; ecall: TEcall }
	type TJumpSession =
		| { itemKind: "ecall"; source: TJumpSource; items: TEcall[]; index: number }
		| { itemKind: "eref"; source: TJumpSource; items: TErefWithFurniture[]; index: number }
	const [jumpSession, setJumpSession] = useState<TJumpSession | null>(null)
	const jumpSessionRef = useRef<TJumpSession | null>(null)
	jumpSessionRef.current = jumpSession
	const [furnitureInitialTab, setFurnitureInitialTab] = useState<"Details" | "Connected callouts">("Details")
	const [furnitureMountEpoch, setFurnitureMountEpoch] = useState(0)

	// Right-click context menu (Copy on a furniture / Paste on empty space). Position is in
	// app-container-relative px. The furniture/point the menu acts on are held in refs so the
	// menu's item handlers read the right target without re-subscribing.
	const [contextMenu, setContextMenu] = useState<{ left: number; top: number; kind: "furniture" | "free" } | null>(
		null,
	)
	const contextMenuFurnitureRef = useRef<TFurniture | null>(null)
	const contextMenuPointRef = useRef<{ pageNumber: number; x: number; y: number } | null>(null)
	const contextMenuEntryRef = useRef<RootEntry | null>(null)
	// Last cursor position over the scroll view, so Ctrl/Cmd+V can paste at the cursor.
	const lastPointerClientRef = useRef<{ x: number; y: number } | null>(null)

	const queryClient = useQueryClient()
	const { success, error } = useMessage()
	const { formatError } = useApiErrorFormatter()
	const { mutateAsync: duplicateFurnitureAsync } = useDuplicateFurniture()

	const [searchParams] = useSearchParams()
	const quotationId = searchParams.get("quotationID")
	const numericQuotationId = quotationId ? Number(quotationId) : undefined
	const numericQuotationIdRef = useRef(numericQuotationId)
	numericQuotationIdRef.current = numericQuotationId
	const { data: quotationDetail } = useGetQuotation(quotationId ? Number(quotationId) : undefined)
	const quotationDetailRef = useRef(quotationDetail)
	quotationDetailRef.current = quotationDetail
	const relatedObjects = quotationDetail?.related_objects
	const relatedObjectsRef = useRef(relatedObjects)
	relatedObjectsRef.current = relatedObjects
	const allEcalls = useMemo(() => relatedObjects?.flatMap((page) => page.ecalls) ?? [], [relatedObjects])
	const allEcallsRef = useRef(allEcalls)
	allEcallsRef.current = allEcalls

	const connectedErefIds = useMemo<number[]>(() => {
		if (!relatedObjects) return []
		const ids: number[] = []
		for (const page of relatedObjects) {
			const linkedIds = new Set<number>()
			for (const f of page.furniture) {
				if (f.eref_id != null) linkedIds.add(f.eref_id)
			}
			// Erefs are sorted by elevation number for navigation; furniture order is
			// handled server-side.
			const sortedConnected = page.erefs
				.filter((eref) => linkedIds.has(eref.id))
				.sort((a, b) =>
					(a.elevation_number ?? "").localeCompare(b.elevation_number ?? "", undefined, { numeric: true }),
				)
			for (const eref of sortedConnected) {
				ids.push(eref.id)
			}
		}
		return ids
	}, [relatedObjects])

	// Only render/track annotations in editor mode. Preview is read-only with no highlights.
	useAnnotations(mode === "editor" ? relatedObjects : undefined)

	// Keep selectedFurniture in sync with refreshed relatedObjects after a refetch
	useEffect(() => {
		const currentId = selectedFurnitureRef.current?.id
		if (!currentId || !relatedObjects) return
		for (const page of relatedObjects) {
			const next = page.furniture.find((f) => f.id === currentId)
			if (next && next !== selectedFurnitureRef.current) {
				selectedFurnitureRef.current = next
				setSelectedFurniture(next)
				return
			}
		}
	}, [relatedObjects])

	// Keep selectedEref in sync with refreshed relatedObjects after a refetch
	useEffect(() => {
		const currentId = selectedErefRef.current?.id
		if (!currentId || !relatedObjects) return
		for (const page of relatedObjects) {
			const eref = page.erefs.find((e) => e.id === currentId)
			if (eref) {
				const furniture = page.furniture.filter((f) => f.eref_id === currentId)
				const next: TErefWithFurniture = { ...eref, furniture }
				selectedErefRef.current = next
				setSelectedEref(next)
				return
			}
		}
	}, [relatedObjects])

	// Keep selectedEcall in sync with refreshed relatedObjects after a refetch
	useEffect(() => {
		const currentId = selectedEcallRef.current?.id
		if (!currentId || !relatedObjects) return
		for (const page of relatedObjects) {
			const ecall = page.ecalls.find((e) => e.id === currentId)
			if (ecall && ecall !== selectedEcallRef.current) {
				selectedEcallRef.current = ecall
				setSelectedEcall(ecall)
				return
			}
		}
	}, [relatedObjects])

	const getShadowRoot = useCallback(
		() => (previewRef.current as unknown as HTMLElement)?.querySelector("apryse-webviewer")?.shadowRoot ?? undefined,
		[previewRef],
	)

	const { open: openPanel, close: closePanel } = useMemo(
		() => createPanelControls(instanceRef, "elevation-references-panel", setIsPanelOpen),
		[],
	)
	const { open: openReferencePanel, close: closeReferencePanel } = useMemo(
		() => createPanelControls(instanceRef, "reference-panel", setIsReferencePanelOpen),
		[],
	)

	const handleErefClick = useCallback(
		(eref: TErefWithFurniture, options: { scroll?: boolean } = { scroll: true }) => {
			selectedErefRef.current = eref
			setSelectedEref(eref)
			setRightPanelMode("eref")
			openReferencePanel()
			if (options.scroll) {
				AnnotationService.scrollToEref(eref.id)
				AnnotationService.selectEref(eref.id)
			}
		},
		[openReferencePanel],
	)

	const handleFurnitureClick = useCallback(
		(furniture: TFurniture, options: { scroll?: boolean } = { scroll: true }) => {
			selectedFurnitureRef.current = furniture
			setSelectedFurniture(furniture)
			setRightPanelMode("furniture")
			setFurnitureInitialTab("Details")
			openReferencePanel()
			if (options.scroll) {
				AnnotationService.scrollToFurniture(furniture.id)
				AnnotationService.selectFurniture(furniture.id)
			}
		},
		[openReferencePanel],
	)

	const handleFurnitureClose = useCallback(() => {
		setSelectedFurniture(undefined)
		selectedFurnitureRef.current = undefined
		if (selectedErefRef.current) {
			setRightPanelMode("eref")
		} else {
			setRightPanelMode(null)
			closeReferencePanel()
		}
	}, [closeReferencePanel])

	const handleEcallClick = useCallback(
		(ecall: TEcall, options: { scroll?: boolean } = { scroll: true }) => {
			selectedEcallRef.current = ecall
			setSelectedEcall(ecall)
			setRightPanelMode("ecall")
			openReferencePanel()
			if (options.scroll) {
				AnnotationService.scrollToEcall(ecall.id)
				AnnotationService.selectEcall(ecall.id)
			}
		},
		[openReferencePanel],
	)

	const handleEcallClose = useCallback(() => {
		setSelectedEcall(undefined)
		selectedEcallRef.current = undefined
		setRightPanelMode(null)
		closeReferencePanel()
	}, [closeReferencePanel])

	const findEcallById = useCallback((ecallId: number): TEcall | undefined => {
		const pages = relatedObjectsRef.current
		if (!pages) return undefined
		for (const page of pages) {
			const ecall = page.ecalls.find((e) => e.id === ecallId)
			if (ecall) return ecall
		}
		return undefined
	}, [])
	const findErefWithFurnitureById = useCallback((erefId: number): TErefWithFurniture | undefined => {
		const pages = relatedObjectsRef.current
		if (!pages) return undefined
		for (const page of pages) {
			const eref = page.erefs.find((e) => e.id === erefId)
			if (eref) {
				const furniture = page.furniture.filter((f) => f.eref_id === erefId)
				return { ...eref, furniture }
			}
		}
		return undefined
	}, [])

	const findFurnitureById = useCallback((furnitureId: number): TFurniture | undefined => {
		const pages = relatedObjectsRef.current
		if (!pages) return undefined
		for (const page of pages) {
			const furniture = page.furniture.find((f) => f.id === furnitureId)
			if (furniture) return furniture
		}
		return undefined
	}, [])

	const openErefInSidebar = useCallback(
		(eref: TErefWithFurniture) => {
			selectedErefRef.current = eref
			setSelectedEref(eref)
			setRightPanelMode("eref")
			openReferencePanel()
			AnnotationService.scrollToEref(eref.id)
			AnnotationService.selectEref(eref.id)
		},
		[openReferencePanel],
	)

	// Returns whether it navigated, so the Alt+← hotkey knows to move keyboard focus into the
	// reopened eref sidebar. No-ops (and returns false) when the furniture has no connected eref.
	const handleNavigateFurnitureToEref = useCallback(
		(furniture: TFurniture): boolean => {
			if (furniture.eref_id == null) return false
			const eref = findErefWithFurnitureById(furniture.eref_id)
			if (!eref) return false
			openErefInSidebar(eref)
			return true
		},
		[findErefWithFurnitureById, openErefInSidebar],
	)

	// Steps through the displayed eref list (connectedErefIds — same order as the left sidebar)
	// with wrap-around. Returns whether it navigated so the Alt+↓/↑ hotkeys can move focus.
	const handleStepConnectedEref = useCallback(
		(direction: "next" | "prev"): boolean => {
			if (connectedErefIds.length === 0) return false
			const currentId = selectedErefRef.current?.id
			const currentIdx = currentId != null ? connectedErefIds.indexOf(currentId) : -1
			const delta = direction === "next" ? 1 : -1
			const nextIdx =
				currentIdx === -1
					? direction === "next"
						? 0
						: connectedErefIds.length - 1
					: (currentIdx + delta + connectedErefIds.length) % connectedErefIds.length
			const eref = findErefWithFurnitureById(connectedErefIds[nextIdx])
			if (!eref) return false
			openErefInSidebar(eref)
			return true
		},
		[connectedErefIds, findErefWithFurnitureById, openErefInSidebar],
	)

	// Alt+↓ / Alt+↑: next / previous eref. Scoped to eref mode only — a no-op (returns false)
	// while a furniture or ecall is selected, per REQ-360 task 2.
	const navigateAdjacentEref = useCallback(
		(direction: "next" | "prev"): boolean => {
			if (!isReferencePanelOpenRef.current || rightPanelModeRef.current !== "eref") return false
			return handleStepConnectedEref(direction)
		},
		[handleStepConnectedEref],
	)
	const navigateNextEref = useCallback(() => navigateAdjacentEref("next"), [navigateAdjacentEref])
	const navigatePrevEref = useCallback(() => navigateAdjacentEref("prev"), [navigateAdjacentEref])

	// Alt+←: from a furniture, open its connected eref. Scoped to furniture mode only — a no-op
	// (returns false) while an eref or ecall is selected, per REQ-360 task 3.
	const navigateFurnitureToParentEref = useCallback((): boolean => {
		if (!isReferencePanelOpenRef.current || rightPanelModeRef.current !== "furniture") return false
		const furniture = selectedFurnitureRef.current
		if (!furniture) return false
		return handleNavigateFurnitureToEref(furniture)
	}, [handleNavigateFurnitureToEref])

	// Keyboard hotkeys for the editor (Alt+1 focus, Tab trap, Enter activation, and the Alt+Arrow
	// right-sidebar navigation below). All hotkey logic + the tinykeys dispatcher live in
	// useEditorHotkeys, next to the hotkey registry. Placed after the navigation callbacks so it
	// can receive them.
	useEditorHotkeys({
		mode,
		isInitialized,
		isModalOpen,
		isPanelOpen,
		isReferencePanelOpen,
		openPanel,
		closePanel,
		closeReferencePanel,
		leftPanelEntryRef: sidebarLeftEntryRef,
		rightPanelEntryRef: sidebarRightEntryRef,
		rightPanelMode,
		selectedEref,
		selectedFurniture,
		selectedEcall,
		onNavigateNextEref: navigateNextEref,
		onNavigatePrevEref: navigatePrevEref,
		onNavigateFurnitureToEref: navigateFurnitureToParentEref,
	})

	const findConnectedEcalls = useCallback((furniture: TFurniture): TEcall[] => {
		return getEcallsForErefId(allEcallsRef.current, furniture.eref_id)
	}, [])

	const applyJumpTarget = useCallback((session: TJumpSession) => {
		if (session.itemKind === "ecall") {
			const target = session.items[session.index]
			selectedEcallRef.current = target
			setSelectedEcall(target)
			setRightPanelMode("ecall")
			AnnotationService.scrollToEcall(target.id)
			AnnotationService.selectEcall(target.id)
		} else {
			const target = session.items[session.index]
			selectedErefRef.current = target
			setSelectedEref(target)
			setRightPanelMode("eref")
			AnnotationService.scrollToEref(target.id)
			AnnotationService.selectEref(target.id)
		}
	}, [])

	const startEcallJumpSession = useCallback(
		(source: TJumpSource, items: TEcall[], startIndex: number) => {
			if (items.length === 0) return
			const clamped = Math.max(0, Math.min(startIndex, items.length - 1))
			const next: TJumpSession = { itemKind: "ecall", source, items, index: clamped }
			jumpSessionRef.current = next
			setJumpSession(next)
			openReferencePanel()
			applyJumpTarget(next)
		},
		[applyJumpTarget, openReferencePanel],
	)

	const startErefJumpSession = useCallback(
		(source: TJumpSource, items: TErefWithFurniture[], startIndex: number) => {
			if (items.length === 0) return
			const clamped = Math.max(0, Math.min(startIndex, items.length - 1))
			const next: TJumpSession = { itemKind: "eref", source, items, index: clamped }
			jumpSessionRef.current = next
			setJumpSession(next)
			openReferencePanel()
			applyJumpTarget(next)
		},
		[applyJumpTarget, openReferencePanel],
	)

	const stepJumpSession = useCallback(
		(delta: number) => {
			const session = jumpSessionRef.current
			if (!session) return
			const nextIndex = Math.max(0, Math.min(session.index + delta, session.items.length - 1))
			if (nextIndex === session.index) return
			const next: TJumpSession = { ...session, index: nextIndex }
			jumpSessionRef.current = next
			setJumpSession(next)
			applyJumpTarget(next)
		},
		[applyJumpTarget],
	)

	const jumpPrev = useCallback(() => stepJumpSession(-1), [stepJumpSession])
	const jumpNext = useCallback(() => stepJumpSession(1), [stepJumpSession])

	const closeJumpSession = useCallback(() => {
		setJumpSession(null)
	}, [])

	const backToJumpSource = useCallback(() => {
		const session = jumpSessionRef.current
		if (!session) return
		setJumpSession(null)
		if (session.source.kind === "furniture") {
			const source = session.source.furniture
			setFurnitureInitialTab("Connected callouts")
			setFurnitureMountEpoch((e) => e + 1)
			selectedFurnitureRef.current = source
			setSelectedFurniture(source)
			setRightPanelMode("furniture")
			openReferencePanel()
			AnnotationService.scrollToFurniture(source.id)
			AnnotationService.selectFurniture(source.id)
		} else if (session.source.kind === "eref") {
			const source = session.source.eref
			selectedErefRef.current = source
			setSelectedEref(source)
			setRightPanelMode("eref")
			openReferencePanel()
			AnnotationService.scrollToEref(source.id)
			AnnotationService.selectEref(source.id)
		} else {
			const source = session.source.ecall
			selectedEcallRef.current = source
			setSelectedEcall(source)
			setRightPanelMode("ecall")
			openReferencePanel()
			AnnotationService.scrollToEcall(source.id)
			AnnotationService.selectEcall(source.id)
		}
	}, [openReferencePanel])

	const handleConnectedCalloutView = useCallback(
		(ecall: TEcall) => {
			const furniture = selectedFurnitureRef.current
			if (!furniture) return
			const connected = findConnectedEcalls(furniture)
			if (connected.length === 0) return
			const idx = connected.findIndex((e) => e.id === ecall.id)
			startEcallJumpSession({ kind: "furniture", furniture }, connected, idx >= 0 ? idx : 0)
		},
		[findConnectedEcalls, startEcallJumpSession],
	)

	const handleErefCalloutView = useCallback(
		(ecall: TEcall) => {
			const eref = selectedErefRef.current
			if (!eref) return
			const connected = getEcallsForErefId(allEcallsRef.current, eref.id)
			if (connected.length === 0) return
			const idx = connected.findIndex((e) => e.id === ecall.id)
			startEcallJumpSession({ kind: "eref", eref }, connected, idx >= 0 ? idx : 0)
		},
		[startEcallJumpSession],
	)

	const handleEcallErefView = useCallback(
		(erefId: number) => {
			const ecall = selectedEcallRef.current
			if (!ecall) return
			const connectedErefIdList = Array.from(
				new Set(ecall.elevation_numbers.map((en) => en.eref_id).filter((id): id is number => id != null)),
			)
			if (connectedErefIdList.length === 0) return
			const erefs = connectedErefIdList
				.map((id) => findErefWithFurnitureById(id))
				.filter((e): e is TErefWithFurniture => e != null)
			if (erefs.length === 0) return
			const idx = erefs.findIndex((e) => e.id === erefId)
			startErefJumpSession({ kind: "ecall", ecall }, erefs, idx >= 0 ? idx : 0)
		},
		[findErefWithFurnitureById, startErefJumpSession],
	)

	const closeContextMenu = useCallback(() => {
		contextMenuFurnitureRef.current = null
		contextMenuPointRef.current = null
		setContextMenu(null)
	}, [])

	const openContextMenuAt = useCallback((clientX: number, clientY: number, kind: "furniture" | "free") => {
		const appEl = jumpingNavTargetContainerRef.current
		if (!appEl) return
		const rect = appEl.getBoundingClientRect()
		setContextMenu({ left: clientX - rect.left, top: clientY - rect.top, kind })
	}, [])

	const copyFurnitureToClipboard = useCallback(
		(furniture: TFurniture) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			copyFurniture(qid, furniture.id)
			success("Object copied")
		},
		[success],
	)

	// Shared tail of every duplicate (paste + Duplicate action): call the duplicate endpoint,
	// reconcile the cache, then draw + select the new object. The placement (x0/y0/page) and
	// eref-link decision are made by each caller and handed in via `body`.
	const submitFurnitureDuplicate = useCallback(
		async (qid: number, sourceFurnitureId: number, body: TDuplicateFurnitureRequest) => {
			try {
				const response = await duplicateFurnitureAsync({ quotationId: qid, furnitureId: sourceFurnitureId, body })
				const data = response?.data as TDuplicateFurnitureResponse | undefined
				const created = data?.this_furniture
				if (!created) return

				// Track whether the furniture actually landed in the cache. insertFurnitureIntoCache
				// no-ops (returns the same ref) when no cached page matches created.page_number —
				// drawing the highlight anyway would leave a canvas-only object that vanishes on the
				// next full re-render. In that case fall back to a refetch instead.
				let furnitureInserted = false
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) => {
					// Insert first (the ordering references the new id), then apply the backend-decided
					// group order so the copy lands in the sidebar where the backend put it — not just
					// appended last. Splice the eref last so its bumped version is reflected too.
					let next = insertFurnitureIntoCache(cached, created)
					if (next !== cached) furnitureInserted = true
					if (data?.ordering) next = reorderFurnitureInCache(next, data.ordering)
					return data?.updated_eref ? spliceErefIntoCache(next, data.updated_eref) : next
				})

				if (!furnitureInserted) {
					queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
					return
				}

				// Draw the new highlight without a full re-render, then select + open its sidebar
				// and center the viewport on the copy so the user sees where it landed.
				AnnotationService.addFurnitureAnnotation(created)
				handleFurnitureClick(created, { scroll: false })
				AnnotationService.selectFurniture(created.id)
				void AnnotationService.scrollToFurniture(created.id)
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
			}
		},
		[duplicateFurnitureAsync, queryClient, handleFurnitureClick, formatError],
	)

	// Same-page eref rule: keep the eref link (and bump its version) only when the copy lands on
	// the source eref's own page (erefs are page-bound); else leave it unassigned. Mutates `body`.
	const applyErefLink = useCallback(
		(body: TDuplicateFurnitureRequest, source: TFurniture, targetPageNumber: number) => {
			if (source.eref_id == null) return
			const sourceEref = findErefWithFurnitureById(source.eref_id)
			if (sourceEref && sourceEref.page_number === targetPageNumber) {
				body.eref_id = source.eref_id
				body.eref_version = sourceEref.version
			}
		},
		[findErefWithFurnitureById],
	)

	const pasteFurnitureAtPoint = useCallback(
		async (point: { pageNumber: number; x: number; y: number }) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			const slot = readFurnitureClipboard()
			if (!slot || slot.quotationId !== qid) return
			const source = findFurnitureById(slot.furnitureId)
			if (!source) return

			const body: TDuplicateFurnitureRequest = { x0: point.x, y0: point.y, page_number: point.pageNumber }
			applyErefLink(body, source, point.pageNumber)
			await submitFurnitureDuplicate(qid, slot.furnitureId, body)
		},
		[findFurnitureById, applyErefLink, submitFurnitureDuplicate],
	)

	// Duplicate a furniture in place (context-menu Duplicate / ⌘D). Drops a copy on the SAME
	// page, offset to the source's right with collision avoidance (computeDuplicatePlacement);
	// only the new top-left + page is sent, the backend deep-clones the rest as work-in-progress.
	const duplicateFurnitureInPlace = useCallback(
		async (source: TFurniture) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			const dims = AnnotationService.getPageDimensions(source.page_number)
			if (!dims) return

			const samePage = relatedObjectsRef.current?.find((p) => p.page_number === source.page_number)
			const occupied = (samePage?.furniture ?? [])
				.filter((f) => f.id !== source.id)
				.map((f) => f.coordinates)
			const { x0, y0 } = computeDuplicatePlacement(
				source.coordinates,
				occupied,
				dims.width,
				dims.height,
			)

			const body: TDuplicateFurnitureRequest = { x0, y0, page_number: source.page_number }
			applyErefLink(body, source, source.page_number)
			await submitFurnitureDuplicate(qid, source.id, body)
		},
		[applyErefLink, submitFurnitureDuplicate],
	)

	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const { annotationManager } = instanceRef.current.Core

		const handler = (annotations: unknown, action: string) => {
			if (action !== "selected") return
			const list = annotations as { Id: string }[]
			if (!list || list.length === 0) return

			const metadata = AnnotationService.getMetadata(list[0].Id)
			if (!metadata) return

			const session = jumpSessionRef.current
			if (session) {
				const sessionItemType = session.itemKind === "ecall" ? "ecall" : "eref"
				const isInSession =
					metadata.entityType === sessionItemType && session.items.some((it) => it.id === metadata.entityId)
				if (!isInSession) {
					setJumpSession(null)
				}
			}

			if (metadata.entityType === "furniture") {
				const furniture = findFurnitureById(metadata.entityId)
				if (furniture) {
					handleFurnitureClick(furniture, { scroll: false })
				}
				return
			}

			if (metadata.entityType === "eref") {
				const eref = findErefWithFurnitureById(metadata.entityId)
				if (eref) {
					handleErefClick(eref, { scroll: false })
				}
				return
			}

			if (metadata.entityType === "ecall") {
				const ecall = findEcallById(metadata.entityId)
				if (ecall) {
					handleEcallClick(ecall, { scroll: false })
				}
			}
		}

		annotationManager.addEventListener("annotationSelected", handler)
		return () => {
			annotationManager.removeEventListener("annotationSelected", handler)
		}
	}, [
		mode,
		isInitialized,
		findErefWithFurnitureById,
		findFurnitureById,
		findEcallById,
		handleErefClick,
		handleFurnitureClick,
		handleEcallClick,
	])

	// Click-to-select bypass (#1119). Once a second Apryse WebViewer instance has existed in the
	// page (e.g. the Documents-tab preview viewer on this drawer), Apryse's edit tool silently
	// stops firing `annotationSelected` on click — so clicking a highlight no longer opens the
	// reference sidebar (it only worked after a left-sidebar select forced a re-render). Disposing
	// that other instance does NOT cure it (Apryse teardown is async and leaves residue). But
	// hit-testing (getAnnotationByMouseEvent) and programmatic selection both still work, so we
	// stand in for the edit tool: hit-test the click and select the entity ourselves. The
	// programmatic selectAnnotation fires `annotationSelected`, which the handler above turns into
	// a sidebar open. We only act while the edit tool is active (leave the ruler etc. alone).
	//
	// When we DO hit one of our highlights we stopPropagation so the broken edit tool never sees
	// the click: in the multi-instance state it treats that same click as an empty-space click and
	// deselects what we just selected — the selection visibly flashes blue and vanishes. Stopping
	// the event keeps our selection (and its resize handles) on screen. Capture phase so we run
	// before Apryse's own handlers; no preventDefault so empty-space clicks still pan/focus.
	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const { documentViewer, Tools } = instanceRef.current.Core
		const scrollEl = documentViewer.getScrollViewElement() as HTMLElement | null
		if (!scrollEl) return

		const onMouseDown = (e: MouseEvent) => {
			if (documentViewer.getToolMode() !== documentViewer.getTool(Tools.ToolNames.EDIT)) return
			const hit = AnnotationService.selectAnnotationFromMouseEvent(e)
			if (hit) {
				e.stopPropagation()
				e.stopImmediatePropagation()
			}
		}
		scrollEl.addEventListener("mousedown", onMouseDown, true)
		return () => scrollEl.removeEventListener("mousedown", onMouseDown, true)
	}, [mode, isInitialized])

	// Copy/paste wiring (REQ-368): right-click a furniture → Copy menu; right-click empty
	// space → Paste menu (only when a furniture was copied for this quotation); Ctrl/Cmd+C
	// copies the selected furniture; Ctrl/Cmd+V pastes at the last cursor position. Editor only.
	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const { documentViewer, annotationManager } = instanceRef.current.Core
		const scrollEl = documentViewer.getScrollViewElement() as HTMLElement | null
		if (!scrollEl) return

		const onPointerMove = (e: MouseEvent) => {
			lastPointerClientRef.current = { x: e.clientX, y: e.clientY }
		}

		const onContextMenu = (e: MouseEvent) => {
			// Suppress the native browser menu inside the document area.
			e.preventDefault()
			lastPointerClientRef.current = { x: e.clientX, y: e.clientY }

			const annot = annotationManager.getAnnotationByMouseEvent(e)
			const metadata = annot ? AnnotationService.getMetadata(annot.Id) : undefined
			if (metadata?.entityType === "furniture") {
				const furniture = findFurnitureById(metadata.entityId)
				if (furniture) {
					AnnotationService.selectFurniture(furniture.id)
					contextMenuFurnitureRef.current = furniture
					contextMenuPointRef.current = null
					openContextMenuAt(e.clientX, e.clientY, "furniture")
					return
				}
			}

			// Copy/paste is furniture-only: right-clicking an elevation reference or callout has no
			// menu action, so suppress the native menu and show nothing (no Paste over them either).
			if (metadata?.entityType === "eref" || metadata?.entityType === "ecall") {
				closeContextMenu()
				return
			}

			// Empty space → Paste, only when a copy exists for this quotation.
			const qid = numericQuotationIdRef.current
			if (qid != null && hasClipboardFor(qid)) {
				const point = AnnotationService.getPagePointFromClient(e.clientX, e.clientY)
				if (point) {
					contextMenuFurnitureRef.current = null
					contextMenuPointRef.current = point
					openContextMenuAt(e.clientX, e.clientY, "free")
					return
				}
			}
			closeContextMenu()
		}

		const onKeyDown = (e: KeyboardEvent) => {
			if (!(e.metaKey || e.ctrlKey)) return
			if (e.code !== "KeyC" && e.code !== "KeyV" && e.code !== "KeyD") return
			// Don't hijack typing in inputs (event.target is retargeted to the host in shadow DOM).
			const target = (e.composedPath?.()[0] as HTMLElement | undefined) ?? (e.target as HTMLElement | null)
			const tag = target?.tagName
			if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return

			if (e.code === "KeyC") {
				// Leave an active text selection to the browser's own copy.
				if ((window.getSelection?.()?.toString() ?? "").trim() !== "") return
				// Copy is furniture-only and single-item. Drive it from the live selection: more
				// than one furniture selected is rejected with a message; nothing copyable
				// (no furniture / only an eref or ecall selected) is silently ignored.
				const selectedFurnitureIds = AnnotationService.getSelectedFurnitureIds()
				if (selectedFurnitureIds.length > 1) {
					e.preventDefault()
					error("You can only copy one object at a time")
					return
				}
				if (selectedFurnitureIds.length === 0) return
				const furniture = findFurnitureById(selectedFurnitureIds[0])
				if (!furniture) return
				e.preventDefault()
				copyFurnitureToClipboard(furniture)
				return
			}

			// Ctrl/Cmd+D → duplicate the selected furniture in place (overrides browser bookmark).
			// Furniture-only and single-item, driven by the live selection — same rules as Copy.
			if (e.code === "KeyD") {
				const selectedFurnitureIds = AnnotationService.getSelectedFurnitureIds()
				if (selectedFurnitureIds.length > 1) {
					e.preventDefault()
					error("You can only duplicate one object at a time")
					return
				}
				if (selectedFurnitureIds.length === 0) return
				const furniture = findFurnitureById(selectedFurnitureIds[0])
				if (!furniture) return
				e.preventDefault()
				void duplicateFurnitureInPlace(furniture)
				return
			}

			// Ctrl/Cmd+V → paste at the last cursor position over the document.
			const qid = numericQuotationIdRef.current
			if (qid == null || !hasClipboardFor(qid)) return
			const last = lastPointerClientRef.current
			if (!last) return
			const point = AnnotationService.getPagePointFromClient(last.x, last.y)
			if (!point) return
			e.preventDefault()
			void pasteFurnitureAtPoint(point)
		}

		scrollEl.addEventListener("mousemove", onPointerMove, { passive: true })
		scrollEl.addEventListener("contextmenu", onContextMenu, true)
		window.addEventListener("keydown", onKeyDown)
		return () => {
			scrollEl.removeEventListener("mousemove", onPointerMove)
			scrollEl.removeEventListener("contextmenu", onContextMenu, true)
			window.removeEventListener("keydown", onKeyDown)
		}
	}, [
		mode,
		isInitialized,
		findFurnitureById,
		openContextMenuAt,
		closeContextMenu,
		copyFurnitureToClipboard,
		pasteFurnitureAtPoint,
		duplicateFurnitureInPlace,
		error,
	])

	// Dismiss the context menu on outside click, scroll, zoom, or Escape.
	useEffect(() => {
		if (!contextMenu) return
		const onOutsidePointerDown = (e: Event) => {
			const menuEl = contextMenuEntryRef.current?.container
			const path = (e as MouseEvent).composedPath?.() ?? []
			if (menuEl && path.includes(menuEl)) return
			closeContextMenu()
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") closeContextMenu()
		}
		const instance = instanceRef.current
		const dv = instance?.Core.documentViewer
		const scrollEl = (dv?.getScrollViewElement() as HTMLElement | null) ?? null
		window.addEventListener("mousedown", onOutsidePointerDown, true)
		window.addEventListener("keydown", onKey)
		scrollEl?.addEventListener("scroll", closeContextMenu, { passive: true })
		dv?.addEventListener("zoomUpdated", closeContextMenu)
		return () => {
			window.removeEventListener("mousedown", onOutsidePointerDown, true)
			window.removeEventListener("keydown", onKey)
			scrollEl?.removeEventListener("scroll", closeContextMenu)
			dv?.removeEventListener("zoomUpdated", closeContextMenu)
		}
	}, [contextMenu, closeContextMenu])

	// Render the context menu into its shadow-DOM overlay at the stored position.
	useEffect(() => {
		const entry = contextMenuEntryRef.current
		if (!entry) return
		const shadowRoot = getShadowRoot()
		if (!contextMenu || !shadowRoot) {
			entry.container.style.display = "none"
			safeRender(contextMenuEntryRef, null)
			return
		}

		entry.container.style.left = `${contextMenu.left}px`
		entry.container.style.top = `${contextMenu.top}px`
		entry.container.style.display = "block"

		const items: TContextMenuItem[] =
			contextMenu.kind === "furniture"
				? [
						{
							key: "copy",
							label: "Copy",
							shortcut: "⌘C",
							onSelect: () => {
								const furniture = contextMenuFurnitureRef.current
								closeContextMenu()
								if (furniture) copyFurnitureToClipboard(furniture)
							},
						},
						{
							key: "duplicate",
							label: "Duplicate",
							shortcut: "⌘D",
							onSelect: () => {
								const furniture = contextMenuFurnitureRef.current
								closeContextMenu()
								if (furniture) void duplicateFurnitureInPlace(furniture)
							},
						},
					]
				: [
						{
							key: "paste",
							label: "Paste",
							shortcut: "⌘V",
							onSelect: () => {
								const point = contextMenuPointRef.current
								closeContextMenu()
								if (point) void pasteFurnitureAtPoint(point)
							},
						},
					]

		safeRender(
			contextMenuEntryRef,
			<StyleProvider container={shadowRoot}>
				<QuotationContextMenu items={items} />
			</StyleProvider>,
		)
	}, [
		contextMenu,
		getShadowRoot,
		copyFurnitureToClipboard,
		pasteFurnitureAtPoint,
		duplicateFurnitureInPlace,
		closeContextMenu,
	])

	// Render toggle button based on panel state
	useEffect(() => {
		const entry = toggleEntryRef.current
		if (!entry) return

		// The elevation-references toggle is an editor affordance; keep it hidden in preview.
		entry.container.style.display = mode !== "editor" || !isDocumentLoaded || isPanelOpen ? "none" : "block"
		if (mode !== "editor") return

		const shadowRoot = getShadowRoot()
		if (!shadowRoot) return

		// When the jumping navigation bar is visible at the top, anchor the toggle
		// to the BOTTOM of the doc area so it doesn't sit underneath the bar.
		const placement = jumpSession ? "bottom" : "top"

		safeRender(
			toggleEntryRef,
			<StyleProvider container={shadowRoot}>
				<ElevationReferencesToggle onOpen={openPanel} placement={placement} />
			</StyleProvider>,
		)
	}, [mode, getShadowRoot, isDocumentLoaded, isPanelOpen, openPanel, previewRef, jumpSession])

	useEffect(() => {
		const entry = jumpingNavEntryRef.current
		if (!entry) return

		const visible = isDocumentLoaded && jumpSession != null && jumpSession.items.length > 0
		entry.container.style.display = visible ? "block" : "none"

		if (visible) {
			positionJumpingNavOverlay(entry.container, jumpingNavDocAreaRef.current, jumpingNavTargetContainerRef.current)
		}

		const shadowRoot = getShadowRoot()
		if (!shadowRoot) return

		if (!visible || !jumpSession) {
			safeRender(jumpingNavEntryRef, null)
			return
		}

		const source = jumpSession.source
		let title: string
		if (source.kind === "furniture") {
			const furnitureLabel =
				(source.furniture.selected_model_number?.model_number && source.furniture.category?.name) || null
			title = furnitureLabel ? `Object ${furnitureLabel}` : "Object"
		} else if (source.kind === "eref") {
			const elevationNumber = source.eref.elevation_number
			const pageName = source.eref.page_name
			const base = elevationNumber ? `Elevation ${elevationNumber}` : "Elevation reference"
			title = pageName ? `${base} — ${pageName}` : base
		} else {
			const roomName = source.ecall.room_name
			title = roomName ? `Callout ${roomName}` : "Elevation callout"
		}

		safeRender(
			jumpingNavEntryRef,
			<StyleProvider container={shadowRoot}>
				<JumpingNavigationBar
					title={title}
					currentIndex={jumpSession.index}
					total={jumpSession.items.length}
					onBack={backToJumpSource}
					onPrev={jumpPrev}
					onNext={jumpNext}
					onClose={closeJumpSession}
				/>
			</StyleProvider>,
		)
	}, [getShadowRoot, isDocumentLoaded, jumpSession, backToJumpSource, jumpPrev, jumpNext, closeJumpSession])

	useEffect(() => {
		const session = jumpSessionRef.current
		if (!session || !relatedObjects) return

		let nextSource: TJumpSource | undefined
		if (session.source.kind === "furniture") {
			const furnitureId = session.source.furniture.id
			for (const page of relatedObjects) {
				const found = page.furniture.find((f) => f.id === furnitureId)
				if (found) {
					nextSource = { kind: "furniture", furniture: found }
					break
				}
			}
		} else if (session.source.kind === "eref") {
			const erefId = session.source.eref.id
			for (const page of relatedObjects) {
				const eref = page.erefs.find((e) => e.id === erefId)
				if (eref) {
					const furniture = page.furniture.filter((f) => f.eref_id === erefId)
					nextSource = { kind: "eref", eref: { ...eref, furniture } }
					break
				}
			}
		} else {
			const ecallId = session.source.ecall.id
			const found = allEcallsRef.current.find((e) => e.id === ecallId)
			if (found) nextSource = { kind: "ecall", ecall: found }
		}
		if (!nextSource) {
			setJumpSession(null)
			return
		}

		const sourceEntity = (s: TJumpSource) =>
			s.kind === "furniture" ? s.furniture : s.kind === "eref" ? s.eref : s.ecall

		if (session.itemKind === "ecall") {
			const sourceErefId =
				nextSource.kind === "furniture"
					? nextSource.furniture.eref_id
					: nextSource.kind === "eref"
						? nextSource.eref.id
						: null
			if (sourceErefId == null) {
				setJumpSession(null)
				return
			}
			const nextItems = getEcallsForErefId(allEcallsRef.current, sourceErefId)
			if (nextItems.length === 0) {
				setJumpSession(null)
				return
			}
			const currentId = session.items[session.index]?.id
			const nextIndex = Math.max(
				0,
				nextItems.findIndex((e) => e.id === currentId),
			)
			const clampedIndex = Math.min(nextIndex, nextItems.length - 1)
			const changed =
				nextItems.length !== session.items.length ||
				clampedIndex !== session.index ||
				sourceEntity(nextSource) !== sourceEntity(session.source) ||
				nextItems.some((e, i) => e.id !== session.items[i]?.id)
			if (changed) {
				setJumpSession({ itemKind: "ecall", source: nextSource, items: nextItems, index: clampedIndex })
			}
			return
		}

		if (nextSource.kind !== "ecall") {
			setJumpSession(null)
			return
		}
		const connectedErefIdList = Array.from(
			new Set(nextSource.ecall.elevation_numbers.map((en) => en.eref_id).filter((id): id is number => id != null)),
		)
		const nextErefItems = connectedErefIdList
			.map((id) => findErefWithFurnitureById(id))
			.filter((e): e is TErefWithFurniture => e != null)
		if (nextErefItems.length === 0) {
			setJumpSession(null)
			return
		}
		const currentErefId = session.items[session.index]?.id
		const nextErefIndex = Math.max(
			0,
			nextErefItems.findIndex((e) => e.id === currentErefId),
		)
		const clampedErefIndex = Math.min(nextErefIndex, nextErefItems.length - 1)
		const erefChanged =
			nextErefItems.length !== session.items.length ||
			clampedErefIndex !== session.index ||
			sourceEntity(nextSource) !== sourceEntity(session.source) ||
			nextErefItems.some((e, i) => e.id !== session.items[i]?.id)
		if (erefChanged) {
			setJumpSession({ itemKind: "eref", source: nextSource, items: nextErefItems, index: clampedErefIndex })
		}
	}, [relatedObjects, findErefWithFurnitureById])

	// Re-render sidebar when isPanelOpen changes to pass latest onClose
	useEffect(() => {
		if (!isPanelOpen) return

		const shadowRoot = getShadowRoot()
		if (!shadowRoot) return

		safeRender(
			sidebarLeftEntryRef,
			<StyleProvider container={shadowRoot}>
				<ElevationReferencesSidebar
					onClose={closePanel}
					onErefClick={handleErefClick}
					onFurnitureClick={handleFurnitureClick}
					relatedObjects={relatedObjects}
					selectedErefPageNumber={selectedEref?.page_number ?? null}
				/>
			</StyleProvider>,
		)
	}, [
		closePanel,
		getShadowRoot,
		handleErefClick,
		handleFurnitureClick,
		isPanelOpen,
		previewRef,
		relatedObjects,
		selectedEref?.page_number,
	])

	const connectedEcallsForSelectedFurniture = useMemo(
		() => getEcallsForErefId(allEcalls, selectedFurniture?.eref_id),
		[selectedFurniture, allEcalls],
	)

	useEffect(() => {
		const shadowRoot = getShadowRoot()
		if (!shadowRoot) return

		let content: ReactNode = null
		if (isReferencePanelOpen) {
			if (rightPanelMode === "furniture" && selectedFurniture) {
				content = (
					<FurnitureSidebar
						key={`${selectedFurniture.id}-${furnitureMountEpoch}`}
						furniture={selectedFurniture}
						relatedObjects={relatedObjects}
						connectedEcalls={connectedEcallsForSelectedFurniture}
						initialTab={furnitureInitialTab}
						onView={handleConnectedCalloutView}
						onClose={handleFurnitureClose}
						onNavigateToEref={handleNavigateFurnitureToEref}
					/>
				)
			} else if (rightPanelMode === "eref" && selectedEref) {
				content = (
					<ReferenceSidebar
						onClose={closeReferencePanel}
						eref={selectedEref}
						ecalls={allEcalls}
						configuration={quotationDetail?.configuration}
						onEcallClick={handleErefCalloutView}
						onFurnitureClick={handleFurnitureClick}
						onPrevConnectedEref={connectedErefIds.length >= 2 ? () => handleStepConnectedEref("prev") : undefined}
						onNextConnectedEref={connectedErefIds.length >= 2 ? () => handleStepConnectedEref("next") : undefined}
					/>
				)
			} else if (rightPanelMode === "ecall" && selectedEcall) {
				content = (
					<EcallSidebar
						key={selectedEcall.id}
						ecall={selectedEcall}
						onClose={handleEcallClose}
						onNavigateToEref={handleEcallErefView}
					/>
				)
			}
		}

		safeRender(sidebarRightEntryRef, <ShadowDomProviders shadowRoot={shadowRoot}>{content}</ShadowDomProviders>)
	}, [
		closeReferencePanel,
		getShadowRoot,
		isReferencePanelOpen,
		selectedEref,
		selectedFurniture,
		selectedEcall,
		rightPanelMode,
		allEcalls,
		previewRef,
		handleFurnitureClose,
		handleEcallClose,
		handleFurnitureClick,
		handleNavigateFurnitureToEref,
		handleStepConnectedEref,
		handleErefCalloutView,
		handleEcallErefView,
		connectedErefIds,
		relatedObjects,
		quotationDetail?.configuration,
		connectedEcallsForSelectedFurniture,
		handleConnectedCalloutView,
		furnitureInitialTab,
		furnitureMountEpoch,
	])

	// Configure the header + tools for the active mode. Called once at init and again whenever
	// `mode` flips, so the SINGLE instance can switch between the editor's custom header (edit
	// tool, sidebars enabled) and the read-only preview (default Apryse header + Share feedback,
	// edit toolbars hidden). The panels/overlays themselves are built once at init and simply
	// stay closed/hidden in preview (their render effects are gated by mode). See #1119.
	const applyHeaderAndTools = useCallback(
		(instance: WebViewerInstance) => {
			const { UI } = instance
			const { documentViewer, Tools } = instance.Core

			// One-time setup: build the editor's custom header and add the preview "Share feedback"
			// button to the default header, then register the custom header ALONGSIDE the default one
			// via addModularHeaders (NOT setModularHeaders([custom]), which replaces — destroying the
			// default header so a later switch back to preview could not restore it; that was the
			// "editor header missing after preview" / blank-header bug). From here on, switching modes
			// only toggles each header's visibility. #1119
			if (!customHeaderAddedRef.current) {
				const headerBlock = {
					type: "customElement",
					dataElement: "customHeaderBlock",
					render: () => {
						const container = document.createElement("div")
						disposeRoot(headerEntryRef)
						headerEntryRef.current = { root: createRoot(container), container }
						headerEntryRef.current.root.render(
							<CustomWebViewerHeader
								instance={instance}
								onClose={onClose}
								onShareFeedback={openQuotationFeedback}
								onSettings={openQuotationSettings}
							/>,
						)
						return container
					},
				}
				const topHeader = new UI.Components.ModularHeader({
					dataElement: "custom-top-header",
					placement: "top",
					gap: 0,
					grow: 1,
					position: "start",
					stroke: false,
					items: [headerBlock],
				})

				// Add the "Share feedback" button to the default header (mirrors useWebviewer).
				if (!previewFeedbackButtonAddedRef.current) {
					const defaultHeader = UI.getModularHeader("default-top-header")
					if (defaultHeader) {
						const customButton = new UI.Components.CustomButton({
							title: "Share feedback",
							label: "Share feedback",
							dataElement: "feedbackButton",
							onClick: () => openFeedbackPanel(),
						})
						const items = defaultHeader.getItems()
						const searchIndex = items.findIndex(
							(item: { dataElement?: string }) => item.dataElement === "searchPanelToggle",
						)
						items.splice(searchIndex > -1 ? searchIndex : items.length, 0, customButton)
						defaultHeader.setItems(items)
						previewFeedbackButtonAddedRef.current = true
					}
				}

				// Add the custom header without disturbing the existing default header(s).
				UI.addModularHeaders([topHeader])
				UI.enableFeatures([UI.Feature.Measurement])

				// Suppress Apryse's built-in popups — our custom selection toolbar + context menu
				// replace them. `contextMenuPopup` = the right-click tool popup (pan / note /
				// highlight / text); `annotationPopup` = the per-annotation style toolbar shown on
				// selecting a highlight.
				UI.disableElements(["annotationPopup", "contextMenuPopup"])

				// `disableElements` does NOT reliably stop the annotation popup from opening in this
				// WebViewer build (it re-shows itself with visibility:visible on every selection), so
				// also force it closed whenever it becomes visible. The visibilityChanged detail is
				// `{ element, isVisible }`.
				UI.addEventListener(
					UI.Events.VISIBILITY_CHANGED,
					(e: { detail?: { element?: string; isVisible?: boolean } }) => {
						if (e?.detail?.element === "annotationPopup" && e.detail.isVisible) {
							UI.closeElements(["annotationPopup"])
						}
					},
				)

				// Disable Apryse's BUILT-IN annotation copy/paste hotkeys. Otherwise Ctrl/Cmd+C copies
				// the selected highlight into Apryse's own clipboard and Ctrl/Cmd+V pastes an offset
				// duplicate annotation — producing a second, mis-placed object on top of our real
				// duplicate. Our window keydown listener handles copy/paste independently.
				UI.hotkeys.off(UI.hotkeys.Keys.CTRL_C)
				UI.hotkeys.off(UI.hotkeys.Keys.COMMAND_C)
				UI.hotkeys.off(UI.hotkeys.Keys.CTRL_V)
				UI.hotkeys.off(UI.hotkeys.Keys.COMMAND_V)

				customHeaderAddedRef.current = true
			}

			if (modeRef.current === "editor") {
				// Editor: show only the custom header, hide the default Apryse header + tools row,
				// hide the default page-nav pills (the modular UI renders them in the
				// "page-nav-floating-header" FloatingHeader, with "page-controls-container" inside; the
				// custom PageNavigationBar replaces them), and use the annotation edit tool so clicks
				// select highlights.
				UI.disableElements([
					"default-top-header",
					"tools-header",
					"page-nav-floating-header",
					"page-controls-container",
				])
				UI.enableElements(["custom-top-header"])
				UI.enableElements(PREVIEW_DISABLED_ELEMENTS)
				documentViewer.setToolMode(documentViewer.getTool(Tools.ToolNames.EDIT))
				return
			}

			// Preview: hide the custom header, restore the default Apryse header (+ tools row) and the
			// default page-nav pills, hide editor affordances, close any editor sidebars, use the pan tool.
			UI.disableElements(["custom-top-header"])
			UI.enableElements([
				"default-top-header",
				"tools-header",
				"page-nav-floating-header",
				"page-controls-container",
			])
			UI.disableElements(PREVIEW_DISABLED_ELEMENTS)
			UI.closeElements(["elevation-references-panel", "reference-panel"])
			setIsPanelOpen(false)
			setIsReferencePanelOpen(false)
			documentViewer.setToolMode(documentViewer.getTool(Tools.ToolNames.PAN))
		},
		[onClose, openQuotationFeedback, openQuotationSettings, openFeedbackPanel],
	)

	// Re-apply header/tools AND re-register services when the mode flips on the already-initialized
	// instance. registerQuotationInstance(…, "editor") attaches AnnotationService (rendering +
	// selection); (…, "preview") runs AnnotationService.cleanup() which also clears any highlights
	// rendered during a prior editor session on this same instance.
	useEffect(() => {
		if (!isInitialized || !instanceRef.current) return
		registerQuotationInstance(instanceRef.current, mode)
		applyHeaderAndTools(instanceRef.current)
	}, [mode, isInitialized, applyHeaderAndTools, registerQuotationInstance])

	// Initialize WebViewer once
	useEffect(() => {
		// The host is a persistent, React-detached <div> (see QuotationViewerModal). It exists
		// before it is attached to the DOM, so guard on isConnected — WebViewer must initialize
		// into an in-document element. The layout effect that parents the host into the active
		// slot runs before this passive effect, and isModalOpen (a dep) re-runs this once open.
		if (!previewRef.current?.isConnected || instanceRef.current || isInitializingRef.current) {
			return
		}

		// Claim the init slot synchronously so a re-run of this effect during the
		// async WebViewer load can't spin up a second viewer on the same container.
		isInitializingRef.current = true

		const initializeViewer = async () => {
			const container = previewRef.current
			if (!container) return

			try {
				loadingTaskRef.current = WebViewer(
					{
						licenseKey: licenseKey,
						path: "/webviewer",
						initialDoc: fileLink,
						css: "/webviewer-quotation.css",
						// Production stability options
						enableOptimizedWorkers: true,
						fullAPI: false,
						useDownloader: false,
					},
					container,
				)
					.then((instance) => {
						// Expose the WebViewer instance globally so automated tests (e.g. Playwright)
						// can verify annotation selection/highlight after navigation. No effect on app
						// behavior — see https://docs.apryse.com/web/faq/react-global-instance
						const globalWindow = window as unknown as {
							__wvInstance?: typeof instance
							__wvClickAnnotation?: (entityId: string) => void
						}
						globalWindow.__wvInstance = instance

						// Test helper: programmatically select the annotation tagged with this entityId,
						// so tests can drive selection by domain id instead of pixel-clicking the canvas.
						// entityId is stored as a string (see AnnotationService.setCustomData), so callers
						// must pass a string (e.g. "501", not 501). No effect on app behavior.
						globalWindow.__wvClickAnnotation = (entityId: string) => {
							const manager = instance.Core.annotationManager
							const annot = manager
								.getAnnotationsList()
								.find((a) => a.getCustomData("entityId") === entityId)
							if (annot) manager.selectAnnotation(annot)
						}

						registerQuotationInstance(instance, modeRef.current)
						const { UI } = instance

						// Inject sidebar styles into the web component's shadow root
						const webComponent = (container as unknown as HTMLElement).querySelector("apryse-webviewer")
						const shadowRoot = webComponent?.shadowRoot
						if (shadowRoot) {
							injectStyles(shadowRoot, leftSidebarStyles)
							injectStyles(shadowRoot, toggleStyles)
							injectStyles(shadowRoot, rightSidebarStyles)
							injectStyles(shadowRoot, numberAndPageStyles)
							injectStyles(shadowRoot, elevationNoteStyles)
							injectStyles(shadowRoot, objectsStyles)
							injectStyles(shadowRoot, elevationCalloutsStyles)
							injectStyles(shadowRoot, connectedCalloutsListStyles)
							injectStyles(shadowRoot, connectedCalloutsTabStyles)
							injectStyles(shadowRoot, jumpingNavigationBarStyles)
							injectStyles(shadowRoot, pageNavigationBarStyles)
							injectStyles(shadowRoot, ecallSidebarStyles)
							injectStyles(shadowRoot, elevationReferenceRowStyles)
							injectStyles(shadowRoot, seriesAndDeliveryStyles)
							injectStyles(shadowRoot, slopeAndOptionsStyles)
							injectStyles(shadowRoot, countertopSeriesStyles)
							injectStyles(shadowRoot, FurnitureModelLabel)
							injectStyles(shadowRoot, footerBarStyles)
							injectStyles(shadowRoot, useReviewableStyles)
							injectStyles(shadowRoot, furnitureSidebarStyles)
							injectStyles(shadowRoot, furnitureHeaderStyles)
							injectStyles(shadowRoot, furnitureImageStyles)
							injectStyles(shadowRoot, erefSelectStyles)
							injectStyles(shadowRoot, categoriesSectionStyles)
							injectStyles(shadowRoot, dimensionsSectionStyles)
							injectStyles(shadowRoot, modelNumberSectionStyles)
							injectStyles(shadowRoot, selectModelNumberModalStyles)
							injectStyles(shadowRoot, modelNumberRowStyles)
							injectStyles(shadowRoot, priceSectionStyles)
							injectStyles(shadowRoot, nameSectionStyles)
							injectStyles(shadowRoot, attachmentsSectionStyles)
							injectStyles(shadowRoot, numberInputRowStyles)
							injectStyles(shadowRoot, segmentedCountRowStyles)
							injectStyles(shadowRoot, notesSectionStyles)
							injectStyles(shadowRoot, furnitureFooterStyles)
							injectStyles(shadowRoot, backButtonStyles)
							injectStyles(shadowRoot, prevNextNavStyles)
							injectStyles(shadowRoot, contextMenuStyles)
							injectStyles(shadowRoot, buttonPrimaryStyles)
							injectStyles(shadowRoot, buttonSecondaryStyles)
							injectStyles(shadowRoot, buttonOutlineStyles)
							injectStyles(shadowRoot, buttonDangerStyles)
							injectStyles(shadowRoot, confirmModalStyles)
							injectStyles(shadowRoot, hotkeyFocusStyles)
						}

						// Configure header + tools for the current mode (re-applied on mode change).
						applyHeaderAndTools(instance)

						// Add left panel for elevation references sidebar
						UI.addPanel({
							dataElement: "elevation-references-panel",
							location: "left",
							render: () => {
								const panelContainer = document.createElement("div")
								panelContainer.style.height = "100%"

								disposeRoot(sidebarLeftEntryRef)
								sidebarLeftEntryRef.current = { root: createRoot(panelContainer), container: panelContainer }

								if (shadowRoot) {
									sidebarLeftEntryRef.current.root.render(
										<StyleProvider container={shadowRoot}>
											<ElevationReferencesSidebar
												onClose={closePanel}
												onErefClick={handleErefClick}
												onFurnitureClick={handleFurnitureClick}
												relatedObjects={relatedObjectsRef.current}
											/>
										</StyleProvider>,
									)
								}

								return panelContainer
							},
						})

						// Add right panel for reference sidebar
						UI.addPanel({
							dataElement: "reference-panel",
							location: "right",
							render: () => {
								const panelContainer = document.createElement("div")
								panelContainer.style.height = "100%"

								disposeRoot(sidebarRightEntryRef)
								sidebarRightEntryRef.current = { root: createRoot(panelContainer), container: panelContainer }

								if (shadowRoot) {
									let initialContent: ReactNode = null
									if (rightPanelModeRef.current === "furniture" && selectedFurnitureRef.current) {
										initialContent = (
											<FurnitureSidebar
												furniture={selectedFurnitureRef.current}
												relatedObjects={relatedObjectsRef.current}
												onClose={handleFurnitureClose}
											/>
										)
									} else if (rightPanelModeRef.current === "ecall" && selectedEcallRef.current) {
										initialContent = <EcallSidebar ecall={selectedEcallRef.current} onClose={handleEcallClose} />
									} else if (selectedErefRef.current) {
										initialContent = (
											<ReferenceSidebar
												onClose={closeReferencePanel}
												eref={selectedErefRef.current}
												ecalls={allEcallsRef.current}
												configuration={quotationDetailRef.current?.configuration}
												onEcallClick={handleEcallClick}
												onFurnitureClick={handleFurnitureClick}
											/>
										)
									}
									sidebarRightEntryRef.current.root.render(
										<ShadowDomProviders shadowRoot={shadowRoot}>{initialContent}</ShadowDomProviders>,
									)
								}

								return panelContainer
							},
						})

						// Create toggle button overlay in the document viewer area
						if (shadowRoot) {
							const docContainer =
								shadowRoot.querySelector(".document-content-container") ||
								shadowRoot.querySelector(".DocumentContainer") ||
								shadowRoot.querySelector("[data-element='documentContainer']")
							const targetContainer = docContainer || shadowRoot.querySelector(".App")

							if (targetContainer) {
								const toggleContainer = document.createElement("div")
								// Wrapper fills the doc area so the inner button can anchor to either
								// top (default) or bottom (when the jumping nav bar is shown). Wrapper
								// is non-interactive; the button itself receives clicks.
								toggleContainer.style.position = "absolute"
								toggleContainer.style.top = "70px"
								toggleContainer.style.bottom = "0"
								toggleContainer.style.left = "0"
								toggleContainer.style.right = "0"
								toggleContainer.style.zIndex = "1000"
								toggleContainer.style.pointerEvents = "none"
								toggleContainer.style.display = "none"

								targetContainer.appendChild(toggleContainer)

								disposeRoot(toggleEntryRef)
								toggleEntryRef.current = { root: createRoot(toggleContainer), container: toggleContainer }
								toggleEntryRef.current.root.render(
									<StyleProvider container={shadowRoot}>
										<ElevationReferencesToggle
											onOpen={() => {
												UI.openElements(["elevation-references-panel"])
												setIsPanelOpen(true)
											}}
										/>
									</StyleProvider>,
								)

								// Jumping navigation bar overlay — must sit BETWEEN the left and right sidebars
								// and BELOW the WebViewer modular header. Both addPanel sidebars and the
								// modular header are overlays inside `.App`, so we can't rely on CSS
								// left:0/right:0 of any single container. Instead we:
								//   parent (wrapper attached to)  = `.App` (full viewport, our coord origin)
								//   reference (doc viewing area)  = documentViewer.getScrollViewElement(),
								//                                   whose getBoundingClientRect() respects
								//                                   the current sidebar + header layout.
								// We recompute on ResizeObserver so the bar tracks sidebar open/close,
								// modular-header changes, and window resize.
								const appContainer =
									(shadowRoot.querySelector(".App") as HTMLElement | null) || (targetContainer as HTMLElement)
								const scrollViewEl = instance.Core.documentViewer.getScrollViewElement() as HTMLElement | null
								const docAreaRefEl: HTMLElement =
									scrollViewEl ??
									((shadowRoot.querySelector(".document-content-container") as HTMLElement | null) || appContainer)

								const jumpingNavContainer = document.createElement("div")
								jumpingNavContainer.style.position = "absolute"
								jumpingNavContainer.style.zIndex = "900"
								jumpingNavContainer.style.pointerEvents = "auto"
								jumpingNavContainer.style.display = "none"

								appContainer.appendChild(jumpingNavContainer)
								jumpingNavTargetContainerRef.current = appContainer
								jumpingNavDocAreaRef.current = docAreaRefEl

								disposeRoot(jumpingNavEntryRef)
								jumpingNavEntryRef.current = { root: createRoot(jumpingNavContainer), container: jumpingNavContainer }
								jumpingNavEntryRef.current.root.render(null)

								// Shares the jumping-nav parent + doc reference so they reposition together.
								const pageNavContainer = document.createElement("div")
								pageNavContainer.style.position = "absolute"
								pageNavContainer.style.zIndex = "900"
								pageNavContainer.style.pointerEvents = "auto"
								pageNavContainer.style.transform = "translate(-50%, -100%)"
								pageNavContainer.style.transition = "opacity 0.25s ease"
								pageNavContainer.style.opacity = "0"

								appContainer.appendChild(pageNavContainer)

								disposeRoot(pageNavEntryRef)
								pageNavEntryRef.current = { root: createRoot(pageNavContainer), container: pageNavContainer }
								pageNavEntryRef.current.root.render(null)

								// Right-click context-menu overlay (Copy / Paste). Positioned at the cursor
								// in app-container-relative px by the render effect; above the page-nav z-index.
								const contextMenuContainer = document.createElement("div")
								contextMenuContainer.style.position = "absolute"
								contextMenuContainer.style.zIndex = "1100"
								contextMenuContainer.style.pointerEvents = "auto"
								contextMenuContainer.style.display = "none"

								appContainer.appendChild(contextMenuContainer)

								disposeRoot(contextMenuEntryRef)
								contextMenuEntryRef.current = {
									root: createRoot(contextMenuContainer),
									container: contextMenuContainer,
								}
								contextMenuEntryRef.current.root.render(null)

								const reposition = () => {
									positionJumpingNavOverlay(
										jumpingNavEntryRef.current?.container,
										jumpingNavDocAreaRef.current,
										jumpingNavTargetContainerRef.current,
									)
									positionPageNavOverlay(
										pageNavEntryRef.current?.container,
										jumpingNavDocAreaRef.current,
										jumpingNavTargetContainerRef.current,
									)
								}
								reposition()
								repositionOverlaysRef.current = reposition

								jumpingNavResizeObserverRef.current?.disconnect()
								const ro = new ResizeObserver(reposition)
								ro.observe(docAreaRefEl)
								ro.observe(appContainer)
								jumpingNavResizeObserverRef.current = ro
							}
						}

						// Panel is closed by default
						setIsPanelOpen(false)

						const { documentViewer: docViewer } = instance.Core
						const showPageNav = () => {
							// Page-nav overlay is an editor affordance only.
							if (modeRef.current !== "editor") return
							if (!isPageNavVisibleRef.current) {
								isPageNavVisibleRef.current = true
								setIsPageNavVisible(true)
							}
							if (pageNavHideTimerRef.current != null) {
								window.clearTimeout(pageNavHideTimerRef.current)
								pageNavHideTimerRef.current = null
							}
							// Keep the bar visible while an input is focused — user is typing.
							if (isPageNavFocusedRef.current) return
							pageNavHideTimerRef.current = window.setTimeout(() => {
								isPageNavVisibleRef.current = false
								setIsPageNavVisible(false)
								pageNavHideTimerRef.current = null
							}, PAGE_NAV_HIDE_DELAY)
						}

						docViewer.addEventListener("documentLoaded", () => {
							setIsDocumentLoaded(true)
							setTotalPages(docViewer.getPageCount())
							setCurrentPage(docViewer.getCurrentPage())
							// Re-assert the annotation-edit tool on every load (incl. modal reopen),
							// so clicks always select highlights and pan/zoom behave normally — editor
							// mode only; preview stays on the pan tool.
							if (modeRef.current === "editor") {
								const { Tools } = instance.Core
								docViewer.setToolMode(docViewer.getTool(Tools.ToolNames.EDIT))
							}
						})

						docViewer.addEventListener("pageNumberUpdated", (page: number) => {
							// During programmatic chevron / page-name navigation, Apryse's scroll
							// animation can briefly fire pageNumberUpdated for the page being left
							// before settling on the target. Ignore those intermediate events.
							if (pageNavTargetRef.current != null && page !== pageNavTargetRef.current) return
							setCurrentPage(page)
							showPageNav()
						})

						docViewer.addEventListener("zoomUpdated", showPageNav)

						const scrollEl = docViewer.getScrollViewElement() as HTMLElement | null
						if (scrollEl) {
							scrollEl.addEventListener("scroll", showPageNav, { passive: true })
							pageNavScrollElRef.current = scrollEl
							pageNavScrollHandlerRef.current = showPageNav
						}

						instanceRef.current = instance
						setIsInitialized(true)
					})
					.catch((error) => {
						console.error("WebViewer initialization failed:", error)
						// Release the slot so a later effect run can retry.
						isInitializingRef.current = false
					})
			} catch (error) {
				console.error("WebViewer initialization failed:", error)
				isInitializingRef.current = false
			}
		}

		initializeViewer()
	}, [
		previewRef,
		registerQuotationInstance,
		applyHeaderAndTools,
		fileLink,
		closePanel,
		closeReferencePanel,
		handleErefClick,
		handleFurnitureClick,
		handleFurnitureClose,
		handleEcallClick,
		handleEcallClose,
		isModalOpen,
	])

	const resetPageNavTimers = useCallback(() => {
		if (pageNavHideTimerRef.current != null) {
			window.clearTimeout(pageNavHideTimerRef.current)
			pageNavHideTimerRef.current = null
		}
		if (pageNavTargetClearTimerRef.current != null) {
			window.clearTimeout(pageNavTargetClearTimerRef.current)
			pageNavTargetClearTimerRef.current = null
		}
		pageNavTargetRef.current = null
	}, [])

	// Cleanup on unmount only — separated so dep changes in the init effect don't destroy roots
	useEffect(() => {
		return () => {
			const toggleEl = toggleEntryRef.current?.container ?? null
			const jumpingNavEl = jumpingNavEntryRef.current?.container ?? null
			const pageNavEl = pageNavEntryRef.current?.container ?? null
			const contextMenuEl = contextMenuEntryRef.current?.container ?? null

			jumpingNavResizeObserverRef.current?.disconnect()
			jumpingNavResizeObserverRef.current = null
			jumpingNavDocAreaRef.current = null
			jumpingNavTargetContainerRef.current = null

			resetPageNavTimers()
			isPageNavVisibleRef.current = false

			if (pageNavScrollElRef.current && pageNavScrollHandlerRef.current) {
				pageNavScrollElRef.current.removeEventListener("scroll", pageNavScrollHandlerRef.current)
			}
			pageNavScrollElRef.current = null
			pageNavScrollHandlerRef.current = null

			disposeRoot(headerEntryRef)
			disposeRoot(sidebarLeftEntryRef)
			disposeRoot(sidebarRightEntryRef)
			disposeRoot(toggleEntryRef)
			disposeRoot(jumpingNavEntryRef)
			disposeRoot(pageNavEntryRef)
			disposeRoot(contextMenuEntryRef)

			queueMicrotask(() => {
				toggleEl?.remove()
				jumpingNavEl?.remove()
				pageNavEl?.remove()
				contextMenuEl?.remove()
			})

			// Tear down the single shared WebViewer instance itself. UI.dispose() removes the
			// Core event listeners registered during init (documentLoaded/pageNumberUpdated/
			// zoomUpdated) and frees the viewer's workers/canvas. Without it, closing the drawer
			// or switching FileInfo tabs unmounts this hook but leaves the Apryse instance alive,
			// so a later reopen spins up a SECOND instance — the multi-instance state this whole
			// flow exists to avoid. #1119
			const instance = instanceRef.current
			instanceRef.current = null
			isInitializingRef.current = false
			if (instance) {
				instance.UI.dispose().catch((error) => {
					console.warn("WebViewer dispose failed:", error)
				})
			}
		}
	}, [resetPageNavTimers])

	const currentPageName = useMemo<string | null>(() => {
		if (!relatedObjects) return null
		// related_objects' page_number is 0-based; the WebViewer uses 1-based pages.
		const page = relatedObjects.find((p) => p.page_number === currentPage - 1)
		return page?.page_name && page.page_name.trim() !== "" ? page.page_name : null
	}, [relatedObjects, currentPage])

	const handlePageChange = useCallback((page: number) => {
		const instance = instanceRef.current
		const dv = instance?.Core.documentViewer
		if (!instance || !dv) return
		pageNavTargetRef.current = page
		setCurrentPage(page)
		if (pageNavTargetClearTimerRef.current != null) {
			window.clearTimeout(pageNavTargetClearTimerRef.current)
		}
		pageNavTargetClearTimerRef.current = window.setTimeout(() => {
			pageNavTargetRef.current = null
			pageNavTargetClearTimerRef.current = null
		}, PAGE_NAV_TARGET_LOCK_MS)
		// Reset to a fixed 100% zoom around the navigation. Apryse re-applies the
		// active FitMode (FitWidth/FitPage) on page change, which silently
		// overrides a numeric zoom set just once — so we:
		//   1) switch FitMode to Zoom (no auto-fit) before the jump
		//   2) jump to the page
		//   3) reapply zoom on the next pageComplete for THIS page, after
		//      Apryse's own post-jump layout has run
		instance.UI.setFitMode(instance.UI.FitMode.Zoom)
		instance.UI.setZoomLevel("100%")
		const handlePageComplete = (completedPage: number) => {
			if (completedPage !== page) return
			dv.removeEventListener("pageComplete", handlePageComplete)
			instance.UI.setZoomLevel("100%")
		}
		dv.addEventListener("pageComplete", handlePageComplete)
		dv.setCurrentPage(page, true)
	}, [])

	const handlePageNavFocusChange = useCallback((focused: boolean) => {
		isPageNavFocusedRef.current = focused
		if (focused) {
			if (pageNavHideTimerRef.current != null) {
				window.clearTimeout(pageNavHideTimerRef.current)
				pageNavHideTimerRef.current = null
			}
			if (!isPageNavVisibleRef.current) {
				isPageNavVisibleRef.current = true
				setIsPageNavVisible(true)
			}
		} else {
			if (pageNavHideTimerRef.current != null) {
				window.clearTimeout(pageNavHideTimerRef.current)
			}
			pageNavHideTimerRef.current = window.setTimeout(() => {
				isPageNavVisibleRef.current = false
				setIsPageNavVisible(false)
				pageNavHideTimerRef.current = null
			}, PAGE_NAV_HIDE_DELAY)
		}
	}, [])

	const handlePageNameNavigate = useCallback(
		(name: string): boolean => {
			const trimmed = name.trim()
			if (trimmed === "") return false
			const lower = trimmed.toLowerCase()
			const match = relatedObjectsRef.current?.find((p) => p.page_name != null && p.page_name.toLowerCase() === lower)
			if (!match) return false
			handlePageChange(match.page_number + 1)
			return true
		},
		[handlePageChange],
	)

	useEffect(() => {
		const entry = pageNavEntryRef.current
		if (!entry) return

		entry.container.style.opacity = isDocumentLoaded && isPageNavVisible ? "1" : "0"
		entry.container.style.pointerEvents = isDocumentLoaded && isPageNavVisible ? "auto" : "none"

		const shadowRoot = getShadowRoot()
		if (!shadowRoot) return

		if (!isDocumentLoaded || totalPages === 0) {
			safeRender(pageNavEntryRef, null)
			return
		}

		safeRender(
			pageNavEntryRef,
			<StyleProvider container={shadowRoot}>
				<PageNavigationBar
					currentPage={currentPage}
					totalPages={totalPages}
					pageName={currentPageName}
					onPageChange={handlePageChange}
					onPageNameNavigate={handlePageNameNavigate}
					onFocusChange={handlePageNavFocusChange}
				/>
			</StyleProvider>,
		)
	}, [
		getShadowRoot,
		isDocumentLoaded,
		isPageNavVisible,
		currentPage,
		totalPages,
		currentPageName,
		handlePageChange,
		handlePageNameNavigate,
		handlePageNavFocusChange,
	])

	// Re-position the floating overlays after entering editor mode. The editor opens in a modal
	// whose scale/zoom open-animation makes getBoundingClientRect return a transformed (shrunk)
	// rect, so the position computed during the transition leaves the page-nav pills too high.
	// A transform doesn't change layout size, so the ResizeObserver never re-fires to correct it —
	// recompute once on the next frame and once after the animation has settled. #1119
	useEffect(() => {
		if (mode !== "editor" || !isDocumentLoaded) return
		const raf = requestAnimationFrame(() => repositionOverlaysRef.current?.())
		const settleTimer = window.setTimeout(() => repositionOverlaysRef.current?.(), MODAL_ANIM_SETTLE_MS)
		return () => {
			cancelAnimationFrame(raf)
			window.clearTimeout(settleTimer)
		}
	}, [mode, isDocumentLoaded, isPageNavVisible])

	// Handle document loading — also re-loads on modal reopen, since closeDocument()
	// runs on close and the document otherwise wouldn't be re-initialized when the same
	// fileLink is opened again.
	useEffect(() => {
		if (instanceRef.current && fileLink && isInitialized && isModalOpen) {
			const { UI } = instanceRef.current
			UI.loadDocument(fileLink)
		}
	}, [fileLink, isInitialized, isModalOpen])

	// Handle cleanup when the modal is closed
	useEffect(() => {
		if (!isModalOpen && instanceRef.current) {
			setIsDocumentLoaded(false)
			setJumpSession(null)
			setFurnitureInitialTab("Details")
			setCurrentPage(1)
			setTotalPages(0)
			setIsPageNavVisible(false)
			isPageNavVisibleRef.current = false
			resetPageNavTimers()
			closeContextMenu()

			// Close both sidebars (clears their UI elements + open flags) and clear the
			// selected entities. The viewer instance is reused across quotations, so
			// without this the right (furniture) panel would reopen with stale content
			// the next time the modal is opened.
			closeReferencePanel()
			closePanel()
			setRightPanelMode(null)
			setSelectedEref(undefined)
			setSelectedFurniture(undefined)
			setSelectedEcall(undefined)
			selectedErefRef.current = undefined
			selectedFurnitureRef.current = undefined
			selectedEcallRef.current = undefined

			try {
				const { Core } = instanceRef.current

				// Close the current document
				if (Core && Core.documentViewer) {
					try {
						Core.documentViewer.closeDocument()
					} catch (error) {
						console.log("Could not close document:", error)
					}
				}
			} catch (error) {
				console.error("Error during cleanup:", error)
			}
		}
	}, [isModalOpen, resetPageNavTimers, closeReferencePanel, closePanel, closeContextMenu])

	return {
		instance: instanceRef.current,
		isInitialized,
	}
}
